import React from "react";
import MoneyInput from "@/components/MoneyInput";
import InfoTip from "@/components/onboarding/InfoTip";

export const LEGAL_ENTITY_OPTIONS = [
  { v: "", label: "Select…" },
  { v: "sole_trader", label: "Sole Trader" },
  { v: "ltd", label: "Limited Company (Ltd)" },
  { v: "partnership", label: "Partnership" },
  { v: "cic", label: "Community Interest Company (CIC)" },
  { v: "other", label: "Other" },
];

export const EMPLOYEES_OPTIONS = [
  { v: "", label: "Select…" },
  { v: "0", label: "Just me" },
  { v: "1", label: "Me + 1 employee" },
  { v: "2-5", label: "Me + 2–5 employees" },
  { v: "6-20", label: "Me + 6–20 employees" },
  { v: "21+", label: "Me + 21 or more employees" },
];

const YEARS_TRADING_OPTIONS = [
  { v: "", label: "Select…" },
  { v: "just_starting", label: "Just starting out" },
  { v: "under_1", label: "Less than 1 year" },
  { v: "1_3", label: "1–3 years" },
  { v: "3_5", label: "3–5 years" },
  { v: "5_plus", label: "5+ years" },
];

const Label = ({ children, required, small }) => (
  <label className={`block ${small ? "text-xs" : "text-sm"} font-semibold mb-1`}>
    {children} {required && <span className="text-red-700">*</span>}
  </label>
);

const Select = ({ value, onChange, options, testid }) => (
  <select className="input" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
    {options.map((o) => (
      <option key={o.v} value={o.v}>{o.label}</option>
    ))}
  </select>
);

export const BusinessStep = ({ inputs, setName, patchBiz }) => {
  const bp = inputs.business_profile;
  return (
    <div className="card space-y-6">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label required>
            Business name
            <InfoTip testid="biz-name-tip">Used on your downloadable forecast reports and by your AI coach in every insight.</InfoTip>
          </Label>
          <input className="input" data-testid="business-name-input" value={inputs.business_name} onChange={(e) => setName(e.target.value)} placeholder="Your business" maxLength={120} />
        </div>
        <div>
          <Label required>Legal entity</Label>
          <Select value={bp.legal_entity} onChange={(v) => patchBiz({ legal_entity: v })} options={LEGAL_ENTITY_OPTIONS} testid="biz-legal-entity" />
        </div>
        <div className="md:col-span-2">
          <Label required>Are you VAT registered?</Label>
          <div className="flex gap-3" role="radiogroup">
            {[["yes", "Yes"], ["no", "No"]].map(([v, l]) => (
              <button
                key={v}
                type="button"
                role="radio"
                aria-checked={bp.vat_registered === v}
                onClick={() => patchBiz({ vat_registered: v })}
                data-testid={`biz-vat-${v}`}
                className={`px-6 py-2 rounded-xl border-[1.5px] border-black font-heading uppercase tracking-wide transition-colors ${bp.vat_registered === v ? "bg-[#FFBD4A]" : "bg-white hover:bg-[#FFF7E5]"}`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="border-t border-black/10 pt-6">
        <div className="section-eyebrow mb-3">A little more about your business</div>
        <p className="text-sm text-black/70 mb-5 leading-relaxed">
          This helps your AI coach tailor every insight, action plan, and story to <em>your</em> specific business, not a generic template. The more honest you are here, the clearer your forecast becomes. No judgement. Just truth.
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label small>Industry / type of business</Label>
            <input className="input" value={bp.industry || ""} onChange={(e) => patchBiz({ industry: e.target.value })} placeholder="e.g. wedding photographer, brand strategist, ceramic studio" data-testid="biz-industry" maxLength={200} />
          </div>
          <div>
            <Label small>
              Number of people in your business
              <InfoTip testid="biz-employees-tip">Only count people you employ, not freelancers or contractors.</InfoTip>
            </Label>
            <Select value={bp.employees} onChange={(v) => patchBiz({ employees: v })} options={EMPLOYEES_OPTIONS} testid="biz-employees" />
          </div>
          <div>
            <Label small>How long you've been trading</Label>
            <Select value={bp.years_trading} onChange={(v) => patchBiz({ years_trading: v })} options={YEARS_TRADING_OPTIONS} testid="biz-years" />
          </div>
          <div className="md:col-span-2">
            <Label small>
              Your biggest challenge right now
              <InfoTip testid="biz-challenge-tip">Even just one honest sentence here supercharges every AI insight you'll see.</InfoTip>
            </Label>
            <textarea className="input min-h-[70px] py-2" value={bp.biggest_challenge || ""} onChange={(e) => patchBiz({ biggest_challenge: e.target.value })} placeholder="e.g. Cash keeps dipping in Q1 and I never know why until it's too late." data-testid="biz-challenge" maxLength={500} />
          </div>
        </div>
      </div>
    </div>
  );
};

export const OpeningCashStep = ({ value, onChange, firstDay, lastDayPrev, highlight }) => (
  <div className="card space-y-5">
    <p className="text-black/80 leading-relaxed">
      Take a quick look at your business bank account(s). What was sitting in there at the start of this month, on <strong>{firstDay}</strong>? That's your closing balance from {lastDayPrev}. If you have more than one business account and/or savings pot, add them all together: we're after your <strong>total cash position</strong>. No need for perfection. Overdrawn? That's okay, pop a minus in front (e.g. -800). This isn't a verdict, it's just information.
    </p>
    <div>
      <label className="block text-sm font-semibold mb-1" data-testid="opening-cash-label">
        Total opening cash balance on {firstDay} (£)
        <InfoTip testid="opening-cash-tip">All your business account(s) together, including any savings or tax pots/sub-accounts.</InfoTip>
      </label>
      <div className={`max-w-xs ${highlight ? "ring-4 ring-[#FFBD4A] rounded-xl" : ""}`}>
        <MoneyInput value={value} onChange={onChange} placeholder="2,500" testid="opening-cash-input" allowNegative />
      </div>
    </div>
  </div>
);

export const PayingYourselfStep = ({ value, onChange, isLtd }) => (
  <div className="card space-y-5">
    <p className="text-black/80 leading-relaxed">
      According to a{" "}
      <a href="https://www.xero.com/uk/media-releases/uk-money-matters-report-cash-flow/" target="_blank" rel="noopener noreferrer" className="underline font-semibold">2023 Xero report</a>
      , 41% of UK small business owners sacrificed their salaries to keep their businesses running. Let's try not to be one of them. Paying yourself isn't indulgence. It's proof your business is working for you, not the other way around.
    </p>
    <div className="bg-[#FFF7E5] border-l-4 border-[#FFBD4A] p-4 rounded-r-lg text-sm" data-testid="pay-yourself-guide">
      <p className="font-semibold mb-2">Not sure what to pay yourself? Start here:</p>
      <ul className="list-disc pl-5 space-y-1 leading-relaxed">
        <li>Add up what you need each month to cover your personal living costs, e.g. rent or mortgage, bills, food, transport.</li>
        <li>Add a little on top for savings and life's extras.</li>
        <li>That's your starting point. It's okay if the business can't carry it yet; your forecast will show you what needs to change.</li>
      </ul>
    </div>
    {isLtd && (
      <p className="text-black/80 leading-relaxed" data-testid="pay-yourself-ltd">
        Paying yourself through your limited company? Pop in the total you take out each month, salary and dividends together. Your accountant will work out the exact split at year end; for your forecast, what matters is the total that leaves the business account.
      </p>
    )}
    <div>
      <label className="block text-sm font-semibold mb-1">
        Monthly personal pay (£)
        <InfoTip testid="pay-yourself-tip">Pay yourself irregularly? Use a monthly average for now.</InfoTip>
      </label>
      <div className="max-w-xs">
        <MoneyInput value={value} onChange={onChange} placeholder="2,000" testid="personal-draw-input" />
      </div>
    </div>
  </div>
);
